import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { makeStyles } from "@material-ui/styles";
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { axiosInstance } from "common/network";
import { StatusBullet } from "components";
import { downloadDidimoFormat } from "services/didimoDownload";

const statusColors = {
  done: "success",
  completed: "success",
  processing: "info",
  pending: "info",
  in_progress: "info",
  error: "danger",
  deleted: "danger",
};

const useStyles = makeStyles((theme) => ({
  key: { fontFamily: "monospace" },
  section: { marginTop: theme.spacing(3) },
  statusContainer: {
    display: "flex",
    alignItems: "center",
  },
  status: { marginRight: theme.spacing(1) },
  statusText: { textTransform: "capitalize", fontWeight: 500 },
  errorBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1, 1.5),
    borderLeft: `3px solid ${theme.palette.error.main}`,
    backgroundColor: theme.palette.action.hover,
    borderRadius: 4,
  },
  errorTitle: { color: theme.palette.error.main },
  errorEntry: {
    "&:not(:first-child)": {
      marginTop: theme.spacing(1),
      paddingTop: theme.spacing(1),
      borderTop: `1px solid ${theme.palette.divider}`,
    },
  },
  chips: { display: "flex", flexWrap: "wrap", gap: theme.spacing(1) },
  metaRow: { display: "flex", gap: theme.spacing(2), padding: "2px 0" },
  metaName: {
    color: theme.palette.text.secondary,
    fontWeight: 600,
    width: 140,
    flexShrink: 0,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  fieldLabel: { fontWeight: 600 },
  spinner: { marginLeft: theme.spacing(1) },
}));

// Modal showing the details of a single didimo. Seeded with the row data passed
// in (instant render), then refreshed from GET /v3/didimos/{key} on open so
// status/progress are current. Falls back to the row data if the refresh fails.
const DidimoDetails = ({ didimo, open, onClose }) => {
  const classes = useStyles();
  const [data, setData] = useState(didimo);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState({});
  // Per-input available variants, e.g. { photo: ["used", "original"] }. "original" is present
  // only when a pre-processing photo edition occurred (the DGP consumed the edited photo).
  const [variants, setVariants] = useState({});

  const key = didimo?.key;

  const refresh = useCallback(async () => {
    if (!key) return;
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        process.env.REACT_APP_API_ROOT + `v3/didimos/${key}`
      );
      setData(response?.data || didimo);
    } catch (e) {
      console.log("DidimoDetails refresh:", e);
      setData(didimo); // fall back to the list row data
    } finally {
      setLoading(false);
    }
  }, [key, didimo]);

  useEffect(() => {
    setData(didimo);
    if (open && key) refresh();
  }, [open, key, didimo, refresh]);

  // Discover which inputs also have an "original" variant (i.e. a photo edition happened),
  // so we can offer both the used and the original file. Best-effort: failures leave no
  // extra button. Reuses each input's self link + "/variants".
  useEffect(() => {
    let cancelled = false;
    const loadVariants = async () => {
      const ins = ((data && data.inputs) || []).filter((i) => i?.__links?.self);
      if (!open || ins.length === 0) {
        setVariants({});
        return;
      }
      const entries = await Promise.all(
        ins.map(async (i) => {
          try {
            const r = await axiosInstance.get(`${i.__links.self}/variants`);
            return [i.name, Array.isArray(r?.data) ? r.data : []];
          } catch (e) {
            return [i.name, []];
          }
        })
      );
      if (!cancelled) setVariants(Object.fromEntries(entries));
    };
    loadVariants();
    return () => {
      cancelled = true;
    };
  }, [open, key, data]);

  const download = async (resource, marker, options) => {
    setDownloading((d) => ({ ...d, [marker]: true }));
    try {
      await downloadDidimoFormat(resource, options);
    } catch (e) {
      console.log("download:", e);
      alert(`Could not download "${resource.name}". Please try again.`);
    } finally {
      setDownloading((d) => {
        const next = { ...d };
        delete next[marker];
        return next;
      });
    }
  };

  const d = data || {};
  const formats = d.transfer_formats || [];
  const inputs = d.inputs || [];
  const metadata = d.meta_data || [];
  const artifacts = d.artifacts || [];

  // Normalize error_details into a list of entries. Current API shape is an
  // array of { preprocessor, error_code, failed_checks }; tolerate the legacy
  // string form (paired with a top-level error_code) so older didimos still render.
  const errorDetails = Array.isArray(d.error_details)
    ? d.error_details
    : d.error_details
    ? [{ error_code: d.error_code, message: String(d.error_details) }]
    : d.error_code
    ? [{ error_code: d.error_code }]
    : [];

  const fields = [
    ["Input type", d.input_type || "—"],
    ["Cost", d.cost != null ? String(d.cost) : "—"],
    ["Created", d.created_at ? moment(d.created_at).format("DD/MM/YYYY HH:mm") : "—"],
    ["Expires", d.expires_at ? moment(d.expires_at).format("DD/MM/YYYY") : "—"],
    ["Favorite", d.is_favorite ? "Yes" : "No"],
    ["Public", d.is_public ? "Yes" : "No"],
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Didimo <span className={classes.key}>{key}</span>
        {loading && <CircularProgress size={16} className={classes.spinner} />}
      </DialogTitle>
      <DialogContent dividers>
        <div className={classes.statusContainer}>
          <StatusBullet
            className={classes.status}
            color={statusColors[d.status] || "neutral"}
            size="sm"
          />
          <Typography variant="body2" className={classes.statusText}>
            {d.status}
            {d.percent != null ? ` - ${d.percent}%` : ""}
          </Typography>
        </div>
        {d.status_message && (
          <Typography variant="body2" color="textSecondary">
            {d.status_message}
          </Typography>
        )}

        {errorDetails.length > 0 && (
          <div className={classes.errorBox}>
            <Typography variant="subtitle2" className={classes.errorTitle} gutterBottom>
              Error details
            </Typography>
            {errorDetails.map((err, i) => (
              <div key={i} className={classes.errorEntry}>
                {err.preprocessor && (
                  <div className={classes.metaRow}>
                    <Typography variant="body2" className={classes.metaName}>
                      preprocessor
                    </Typography>
                    <Typography variant="body2">{err.preprocessor}</Typography>
                  </div>
                )}
                {err.error_code && (
                  <div className={classes.metaRow}>
                    <Typography variant="body2" className={classes.metaName}>
                      error code
                    </Typography>
                    <Typography variant="body2">{err.error_code}</Typography>
                  </div>
                )}
                {err.failed_checks &&
                  typeof err.failed_checks === "object" &&
                  Object.entries(err.failed_checks).map(([check, message]) => (
                    <div key={check} className={classes.metaRow}>
                      <Tooltip title={check} arrow>
                        <Typography variant="body2" className={classes.metaName}>
                          {check}
                        </Typography>
                      </Tooltip>
                      <Typography variant="body2">{String(message)}</Typography>
                    </div>
                  ))}
                {err.message && (
                  <Typography variant="body2">{err.message}</Typography>
                )}
              </div>
            ))}
          </div>
        )}

        <Grid container spacing={2} className={classes.section}>
          {fields.map(([label, value]) => (
            <Grid item xs={6} sm={4} key={label}>
              <Typography
                variant="caption"
                color="textSecondary"
                display="block"
                className={classes.fieldLabel}
              >
                {label}
              </Typography>
              <Typography variant="body2">{value}</Typography>
            </Grid>
          ))}
        </Grid>

        <div className={classes.section}>
          <Typography variant="subtitle2" gutterBottom>
            Available formats
          </Typography>
          {formats.length > 0 ? (
            <div className={classes.chips}>
              {formats.map((format) => {
                const marker = `format:${format.name}`;
                return (
                  <Button
                    key={format.name}
                    variant="outlined"
                    color="primary"
                    size="small"
                    style={{ textTransform: "none", minWidth: 0 }}
                    disabled={!!downloading[marker]}
                    startIcon={
                      downloading[marker] ? (
                        <CircularProgress size={14} />
                      ) : undefined
                    }
                    onClick={() => download(format, marker)}
                  >
                    {format.name}
                  </Button>
                );
              })}
            </div>
          ) : (
            <Typography variant="body2" color="textSecondary">
              None available
            </Typography>
          )}
        </div>

        {inputs.length > 0 && (
          <div className={classes.section}>
            <Typography variant="subtitle2" gutterBottom>
              Inputs
            </Typography>
            <div className={classes.chips}>
              {inputs.map((input) => {
                const marker = `input:${input.name}`;
                const hasOriginal = (variants[input.name] || []).includes("original");
                const originalMarker = `${marker}:original`;
                const self = input?.__links?.self;
                const originalSelf = self
                  ? `${self}${self.includes("?") ? "&" : "?"}variant=original`
                  : null;
                return (
                  <React.Fragment key={input.name}>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      style={{ textTransform: "none", minWidth: 0 }}
                      disabled={!!downloading[marker]}
                      startIcon={
                        downloading[marker] ? (
                          <CircularProgress size={14} />
                        ) : undefined
                      }
                      onClick={() => download(input, marker, { newTab: true })}
                    >
                      {hasOriginal ? `${input.name} (used)` : input.name}
                    </Button>
                    {hasOriginal && originalSelf && (
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        style={{ textTransform: "none", minWidth: 0 }}
                        disabled={!!downloading[originalMarker]}
                        startIcon={
                          downloading[originalMarker] ? (
                            <CircularProgress size={14} />
                          ) : undefined
                        }
                        onClick={() =>
                          download(
                            { name: `${input.name} (original)`, __links: { self: originalSelf } },
                            originalMarker,
                            { newTab: true }
                          )
                        }
                      >
                        {`${input.name} (original)`}
                      </Button>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        {metadata.length > 0 && (
          <div className={classes.section}>
            <Typography variant="subtitle2" gutterBottom>
              Metadata
            </Typography>
            {metadata.map((m) => (
              <div key={m.name} className={classes.metaRow}>
                <Tooltip title={m.name} arrow>
                  <Typography variant="body2" className={classes.metaName}>
                    {m.name}
                  </Typography>
                </Tooltip>
                <Typography variant="body2">{String(m.value)}</Typography>
              </div>
            ))}
          </div>
        )}

        {artifacts.length > 0 && (
          <div className={classes.section}>
            <Typography variant="subtitle2" gutterBottom>
              Artifacts
            </Typography>
            <div className={classes.chips}>
              {artifacts.map((a) => (
                <Chip key={a.name} label={a.name} size="small" />
              ))}
            </div>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

DidimoDetails.propTypes = {
  didimo: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

export default DidimoDetails;
